/*
 * 서버 시각 해석은 `@oshimap/domain` 한 곳에 있습니다. 여기서 다시 구현하면
 * 같은 규칙의 사본이 둘이 되고, 언젠가 한쪽만 고쳐집니다 — 이 규칙은 이미
 * 세 번 깨졌습니다. 부르는 쪽은 그대로 `@oshimap/utils` 에서 씁니다.
 */
export { parseServerDate } from "@oshimap/domain";
import { parseServerDate } from "@oshimap/domain";

export function formatDateRange(startsAt?: string | null, endsAt?: string | null): string {
  const s = parseServerDate(startsAt);
  const e = parseServerDate(endsAt);
  if (!s || !e) return "—";
  const f = (d: Date) => `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
  const short = (d: Date) => `${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
  return s.getFullYear() === e.getFullYear() ? `${f(s)} – ${short(e)}` : `${f(s)} – ${f(e)}`;
}

export function formatDay(v?: string | null): string {
  const d = parseServerDate(v);
  if (!d) return "—";
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

/** 여권 월별 집계 키 (서버 형식 "YYYY-MM"). */
export function monthKey(v?: string | null): string | null {
  const d = parseServerDate(v);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}` : null;
}

function pad(n: number): string { return n < 10 ? `0${n}` : String(n); }

/**
 * 알림·기록처럼 "언제였는지" 가 먼저 읽혀야 하는 곳에 쓴다.
 * 미래 시각은 상대 표기가 어색하므로 날짜로 떨어뜨린다.
 */
export function relativeDay(v?: string | null, now: Date = new Date()): string {
  const d = parseServerDate(v);
  if (!d) return "";
  const ms = now.getTime() - d.getTime();
  if (ms < 0) return formatDay(v);
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "어제";
  if (day < 7) return `${day}일 전`;
  return formatDay(v);
}

/**
 * 앞으로 남은 시간. 재시도 대기처럼 **분 단위가 의미 있는** 곳에 쓴다.
 *
 * `relativeDay` 는 미래를 날짜로 떨어뜨린다 — 행사 날짜에는 그게 맞다.
 * 하지만 "12분 뒤 재시도"를 "2026.09.09"로 보여주면 운영자가 기다려야 할
 * 시간을 알 수 없다. 두 쓰임이 다르므로 함수를 나눈다.
 */
export function untilLabel(v?: string | null, now: Date = new Date()): string {
  const d = parseServerDate(v);
  if (!d) return "";
  const ms = d.getTime() - now.getTime();
  if (ms <= 0) return "지금";
  const min = Math.round(ms / 60_000);
  if (min < 1) return "1분 이내";
  if (min < 60) return `${min}분 뒤`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 뒤`;
  return formatDay(v);
}

/**
 * 날짜 + 시:분. **분이 판단을 바꾸는 곳**에 쓴다.
 *
 * 검수 화면에서 후보의 시작·종료 시각이 `2026-10-01T00:00:00+00:00` 그대로
 * 찍히고 있었다. 사람이 읽으라고 만든 화면에 기계가 주고받는 문자열을 그냥
 * 두면, 운영자는 매번 머릿속에서 시간대를 옮겨야 한다.
 *
 * `formatDay` 로 대신하지 않는다 — 팝업 오픈이 오전인지 저녁인지가 검수에서
 * 실제로 갈리는 정보다. 날짜만 보여주면 그 차이가 조용히 사라진다.
 * 보는 사람의 시간대로 옮겨 적는다. 서버 계약(타임존 없는 ISO = UTC)의 해석은
 * `parseServerDate` 한 곳에서만 한다.
 */
export function formatDateTime(v?: string | null): string {
  const d = parseServerDate(v);
  if (!d) return "—";
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 보는 사람 기준 **오늘 날짜** ("YYYY-MM-DD").
 *
 * `new Date().toISOString().slice(0, 10)` 은 UTC 달력 날짜다. 한국에서
 * 00~09시에는 어제가 나온다. 방문 기록 화면이 그렇게 하고 있었고, 기본값이
 * 어제였을 뿐 아니라 `max` 도 어제라서 **아침에 다녀온 곳을 오늘로 기록할
 * 방법이 없었다.** 하루 중 아홉 시간 동안 그랬다.
 *
 * 여기서는 서버 시각을 해석하지 않는다. 지금 이 사람의 달력이 며칠인지다.
 */
export function todayISODate(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
